import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  CreditCard,
  Activity,
  Plus,
  BarChart3,
  Receipt,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  Clock,
  Server,
  Database,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {greeting()}
            {user?.firstName ? `, ${user.firstName}` : ""}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Platform overview and tenant management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/tenants")}>
            <Building2 className="w-4 h-4 mr-1.5" />
            Tenants
          </Button>
          <Button size="sm" onClick={() => navigate("/tenants")}>
            <Plus className="w-4 h-4 mr-1.5" />
            New Tenant
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Tenants"
          value="12"
          icon={<Building2 className="w-5 h-5" />}
          iconBg="bg-primary/5 text-primary"
          trend={{ value: "+2", direction: "up", label: "this month" }}
        />
        <StatCard
          label="Active Users"
          value="1,234"
          icon={<Users className="w-5 h-5" />}
          iconBg="bg-primary/5 text-primary"
          trend={{ value: "+18%", direction: "up", label: "vs last month" }}
        />
        <StatCard
          label="Monthly Revenue"
          value="₦45,678"
          icon={<CreditCard className="w-5 h-5" />}
          iconBg="bg-primary/5 text-primary"
          trend={{ value: "+12%", direction: "up", label: "vs last month" }}
        />
        <StatCard
          label="System Health"
          value="99.9%"
          icon={<Activity className="w-5 h-5" />}
          iconBg="bg-emerald-50 text-emerald-600"
          statusColor="text-emerald-600"
        />
      </div>

      {/* System Status Bar */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SystemMetric
              icon={<Server className="w-4 h-4 text-emerald-500" />}
              label="API Server"
              value="Operational"
              status="healthy"
            />
            <SystemMetric
              icon={<Database className="w-4 h-4 text-emerald-500" />}
              label="Database"
              value="Operational"
              status="healthy"
            />
            <SystemMetric
              icon={<Globe className="w-4 h-4 text-emerald-500" />}
              label="CDN"
              value="Operational"
              status="healthy"
            />
            <SystemMetric
              icon={<Clock className="w-4 h-4 text-primary" />}
              label="Avg Response"
              value="142ms"
              status="healthy"
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tenants (2/3 width) */}
        <Card className="lg:col-span-2 border border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-400" />
                Recent Tenants
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/tenants")}
                className="text-xs"
              >
                View All
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <TenantRow
                name="City Public Library"
                plan="Professional"
                status="active"
                users={87}
                createdAt="2 days ago"
              />
              <TenantRow
                name="University of Lagos Library"
                plan="Enterprise"
                status="active"
                users={342}
                createdAt="5 days ago"
              />
              <TenantRow
                name="Central State Library"
                plan="Starter"
                status="trial"
                users={12}
                createdAt="1 week ago"
              />
              <TenantRow
                name="Community Reading Hub"
                plan="Professional"
                status="active"
                users={56}
                createdAt="2 weeks ago"
              />
              <TenantRow
                name="National Archives Library"
                plan="Enterprise"
                status="active"
                users={210}
                createdAt="3 weeks ago"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions (1/3 width) */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <QuickAction
                icon={<Plus className="w-4 h-4" />}
                label="Create New Tenant"
                description="Set up a new library instance"
                onClick={() => navigate("/tenants")}
                highlighted
              />
              <QuickAction
                icon={<Receipt className="w-4 h-4" />}
                label="Billing Plans"
                description="Subscriptions and payments"
                onClick={() => navigate("/billing-plans")}
              />
              <QuickAction
                icon={<Building2 className="w-4 h-4" />}
                label="Manage Tenants"
                description="View and manage library instances"
                onClick={() => navigate("/tenants")}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-400" />
              Revenue by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <PlanRevenue plan="Enterprise" revenue="₦28,400" percentage={62} color="bg-primary" />
              <PlanRevenue
                plan="Professional"
                revenue="₦12,890"
                percentage={28}
                color="bg-primary/80"
              />
              <PlanRevenue plan="Starter" revenue="₦4,388" percentage={10} color="bg-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-400" />
              Platform Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <ActivityMetric label="API Requests (24h)" value="2.4M" change="+8%" direction="up" />
              <ActivityMetric
                label="New Registrations (7d)"
                value="156"
                change="+23%"
                direction="up"
              />
              <ActivityMetric label="Checkouts (7d)" value="8,924" change="-3%" direction="down" />
              <ActivityMetric label="Storage Used" value="1.2 TB" change="+5%" direction="up" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  iconBg,
  trend,
  statusColor,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  trend?: { value: string; direction: "up" | "down"; label: string };
  statusColor?: string;
}) {
  return (
    <Card className="border border-slate-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${statusColor || "text-slate-900"}`}>{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-1.5">
              {trend.direction === "up" ? (
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />
              )}
              <span
                className={`text-xs font-medium ${trend.direction === "up" ? "text-emerald-600" : "text-red-600"}`}
              >
                {trend.value}
              </span>
              <span className="text-xs text-slate-400">{trend.label}</span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function SystemMetric({
  icon,
  label,
  value,
  status,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  status: "healthy" | "degraded" | "down";
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <div className="flex items-center gap-1.5">
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                status === "healthy"
                  ? "bg-emerald-500"
                  : status === "degraded"
                    ? "bg-amber-500"
                    : "bg-red-500"
              }`}
            />
            <p className="text-sm font-medium text-slate-900">{value}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TenantRow({
  name,
  plan,
  status,
  users,
  createdAt,
}: {
  name: string;
  plan: string;
  status: "active" | "trial" | "suspended";
  users: number;
  createdAt: string;
}) {
  const statusStyles = {
    active: "bg-emerald-50 text-emerald-700",
    trial: "border border-amber-200 bg-amber-50 text-slate-900",
    suspended: "bg-red-50 text-red-700",
  };

  return (
    <div className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-500">
            {plan} &middot; {users} users
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusStyles[status]}`}
        >
          {status}
        </span>
        <span className="text-xs text-slate-400 hidden sm:inline">{createdAt}</span>
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left cursor-pointer group ${
        highlighted ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          highlighted
            ? "bg-primary/10 text-primary"
            : "bg-slate-100 text-slate-600 group-hover:bg-primary/5 group-hover:text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${highlighted ? "text-primary" : "text-slate-900"}`}>
          {label}
        </p>
        <p className={`text-xs ${highlighted ? "text-primary" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
    </button>
  );
}

function PlanRevenue({
  plan,
  revenue,
  percentage,
  color,
}: {
  plan: string;
  revenue: string;
  percentage: number;
  color: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-900">{plan}</span>
        <span className="text-sm font-semibold text-slate-900">{revenue}</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-1">{percentage}% of total</p>
    </div>
  );
}

function ActivityMetric({
  label,
  value,
  change,
  direction,
}: {
  label: string;
  value: string;
  change: string;
  direction: "up" | "down";
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">{value}</span>
        <span
          className={`flex items-center gap-0.5 text-xs font-medium ${
            direction === "up" ? "text-emerald-600" : "text-red-600"
          }`}
        >
          {direction === "up" ? (
            <ArrowUpRight className="w-3 h-3" />
          ) : (
            <ArrowDownRight className="w-3 h-3" />
          )}
          {change}
        </span>
      </div>
    </div>
  );
}
